import { useEffect, useRef, useState } from "react";
import apiClient from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import useBusinessResource from "./useBusinessResource";

export const numberingHeaders = companyId => ({ "X-Company-Id": String(companyId) });
export const numberingErrorMessage = error => error?.response?.data?.error || error?.message || "Impossible de vérifier la numérotation des factures.";

export function useInvoiceNumbering({ companyId, warehouseId = null, active = true }) {
  const { user } = useAuth();
  return useBusinessResource({
    resource: "invoice-numbering-settings", userId: user?.id, companyId, warehouseId,
    scopeLevel: warehouseId ? "warehouse" : "company", moduleScope: "commercial",
    networkMode: "always",
    active, enabled: Boolean(active && companyId),
    load: async signal => (await apiClient.get("/api/invoice-numbering/settings", {
      signal, headers: numberingHeaders(companyId), params: { company_id: companyId, ...(warehouseId ? { warehouse_id: warehouseId } : {}) },
    })).data,
  });
}

export function useManualInvoiceNumber({ companyId, warehouseId, active = true }) {
  const [value, setValue] = useState("");
  const latestValue = useRef(value); latestValue.current = value;
  const { user, hasPermission } = useAuth();
  const canAssign = hasPermission("Gestion Commerciale.Ventes.Ventes.assign_invoice_number");
  const resource = useInvoiceNumbering({ companyId, warehouseId, active: active && canAssign });
  const scope = `${user?.id}:${companyId}:${warehouseId}:${active}`;
  const current = useRef(scope); current.current = scope;
  useEffect(() => { current.current = scope; setValue(""); return () => { current.current = null; }; }, [scope]);
  return { value, setValue, resource, canAssign, visible: active && (Boolean(value) || (canAssign && (Boolean(resource.data?.manual_allowed) || Boolean(resource.error)))),
    async prepare() {
      if (!value.trim()) return undefined;
      if (!canAssign) throw new Error("La saisie manuelle n'est plus autorisée. Videz ce champ pour utiliser la numérotation automatique.");
      const captured = scope;
      const fresh = await resource.refresh({ force: true });
      if (current.current !== captured) throw new Error("Le contexte a changé. Rouvrez la vente ; la saisie précédente n'a pas été envoyée.");
      if (latestValue.current !== value) throw new Error("Le numéro a changé pendant la vérification. Votre saisie est conservée ; réessayez.");
      if (!fresh.manual_allowed || !fresh.can_assign) throw new Error("La saisie manuelle du numéro n'est plus autorisée. Votre saisie est conservée ; videz ce champ pour utiliser la numérotation automatique.");
      return value.trim().toUpperCase();
    },
  };
}
